import { ApolloClient, HttpLink, ApolloLink } from "@apollo/client";
import { useMemo } from "react";

import { SchemaLink } from "@apollo/client/link/schema";
import { ErrorLink } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { PersistedQueryLink } from "@apollo/client/link/persisted-queries";

import cache from "@/apollo/cache/index";
import createDataSources from "@/apollo/datasources/index";
import { AUTH_LOGOUT_MUTATION, AUTH_USER } from "@/apollo/queries/user";
import schema from "@/apollo/schemas/index";
import routes from "@/lib/routes";
import { sha256 } from "crypto-hash";

let apolloClient;

// ユーザーと選択しているプロフィールののratingでキャッシュされるようにextensionsに値をいれる
const AddExtensionsLink = new ApolloLink((operation, forward) => {
  let user;
  const context = operation.getContext();

  if (context?.user) {
    user = context.user;
  }
  if (!user) {
    const client = initializeApollo();
    try {
      user = client.readQuery({ query: AUTH_USER })?.user;
    } catch (e) {}
  }

  if (user) {
    operation.extensions.user = {
      uuid: user.uuid,
      rating: user.currentProfile?.rating,
    };
  }

  return forward(operation);
});

// SSRとCSRでリンクを変える
function createIsomorphLink(req = null, res = null) {
  if (typeof window === "undefined") {
    return new SchemaLink({
      schema,
      context: () => {
        const dataSources = createDataSources({ req });
        const context = {
          req,
          res,
          dataSources,
        };
        Object.values(dataSources).forEach((dataSource) => {
          if (dataSource.initialize) {
            dataSource.initialize({
              context,
            });
          }
        });
        return context;
      },
    });
  } else {
    const logout = async () => {
      const client = initializeApollo();
      // cookieからトークンを削除するためにlogoutをコールする
      await client.mutate({
        mutation: AUTH_LOGOUT_MUTATION,
        variables: { force: true },
      });
      await client.clearStore();
      window.location.replace(routes["401"]);
    };
    return ApolloLink.from([
      new ErrorLink(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach(
            async ({ message, location, path, extensions }) => {
              console.log(
                `[GraphQL Error] Message: ${message}, location: ${location}, path: ${path}, extensions: ${JSON.stringify(
                  extensions
                )}`
              );
              // authエラーだったら
              // TODO:
              if (
                (message && message.includes("Unauthorized")) ||
                extensions?.response?.status == 401 ||
                extensions?.code === "UNAUTHENTICATED"
              ) {
                const client = initializeApollo();
                const { data } = await client.query({
                  query: AUTH_USER,
                  fetchPolicy: "network-only",
                });
                const loggedIn = !!data?.user;
                if (loggedIn) {
                  await logout();
                }
              }
            }
          );
        }
        if (networkError) {
          // console.log(networkError.response);
          // console.log(networkError.statusCode);
          // console.log(`[Network Error] ${networkError}`);
          if (networkError.statusCode == 401) {
            logout().then(() => console.log("401 error logout exec"));
          }
        }
      }),
      new RetryLink({
        delay: { initial: 300, max: Infinity, jitter: true },
        attempts: (count, operation, error) => {
          return (
            !!error &&
            operation.operationName != "Subscribe" &&
            operation.operationName != "PurchaseExecute"
          );
        },
      }),

      new PersistedQueryLink({
        sha256,
        useGETForHashedQueries: true,
      }),
      AddExtensionsLink,
      new HttpLink({
        uri: "/api/graphql",
        credentials: "same-origin",
      }),
    ]);
  }
}

export function createApolloClient(req = null, res = null) {
  const client = new ApolloClient({
    ssrMode: typeof window === "undefined",
    cache: cache(),
    link: createIsomorphLink(req, res),
    ssrForceFetchDelay: 100,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
      },
    },
    useGETForQueries: true,
  });

  return client;
}

export function initializeApollo(initialState = null, req = null, res = null) {
  const _apolloClient = apolloClient ?? createApolloClient(req, res);

  if (initialState) {
    const existingCache = _apolloClient.extract();
    _apolloClient.cache.restore({ ...existingCache, ...initialState });
  }

  // For SSG and SSR always create a new Apollo Client
  if (typeof window === "undefined") return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;
  return _apolloClient;
}

export function useApollo(initialState) {
  const store = useMemo(() => initializeApollo(initialState), [initialState]);
  return store;
}
